import { useState } from "react";
import Groups2Icon from "@mui/icons-material/Groups2";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useScriptMode } from "../../../../contexts/ScriptModeContext";
import { useGetGroupFeedQuery } from "../../../../api/groupsApi";
import InsightsSkeleton from "./InsightsSkeleton";
import { titleProgress } from "./insightsFormat";
import { soft } from "./softClasses";
import { Medal, TitleThumb } from "../../../titles/titleParts";
import { titleImageOf } from "../../../../lib/titleImage";
import { TitleDialog } from "../../../titles/TitlesPage";
import { conditionText, limitingCondition } from "../../../../lib/titleProgress";

/**
 * Ünvanlar: grubun İlim Meclisi ünvanları sayfaya yayılmış liste. Özet
 * üstte sabit, satırlar kayar; satıra (ve özetteki en yüksek ünvana)
 * basınca ünvan penceresi salt okunur açılır: şartı, ilerlemesi ve anlamı.
 * Ünvanlar dönemden bağımsız (grubun biriktirdiği meclis günleri).
 */
export default function GroupTitlesView({ groupId }) {
  const { t } = useScriptMode();
  const [selected, setSelected] = useState(null);
  const { data, isLoading } = useGetGroupFeedQuery({ groupId }, { skip: !groupId });
  const titles = data?.data?.titles;

  if (isLoading || !titles) return <InsightsSkeleton layout="titles" rows={6} />;

  const highest = titles.highest;
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className={`${soft.summary} flex flex-col gap-2.5`}>
        <button
          type="button"
          data-highest-title
          disabled={!highest}
          onClick={() => setSelected({ ...highest, family: highest.family || "meclis", earned: true })}
          className="flex items-center gap-3 text-left rounded-xl disabled:cursor-default"
        >
          <TitleThumb
            title={highest}
            family="meclis"
            size={48}
            fallback={
              <span
                className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 text-white"
                style={{ backgroundColor: highest?.color || "rgba(143, 201, 202, 0.35)" }}
              >
                <WorkspacePremiumIcon aria-hidden />
              </span>
            }
          />
          <div className="min-w-0">
            <div className={`text-[17px] font-medium truncate ${soft.ink}`}>
              {highest?.name || t("Henüz ünvan yok")}
            </div>
            <div className={`text-[13px] truncate ${soft.muted}`}>
              {`${titles.family?.name || ""} · ${titles.value || 0} ${t("meclis günü")} · ${
                titles.earnedCount || 0
              }/${(titles.list || []).length}`}
            </div>
          </div>
        </button>
        {/* Grup küçükse ünvan işlemiyor; sebebi açıkça yazılıyor. */}
        {titles.eligible === false ? (
          <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-terakkiBrandMist text-terakkiPrimaryDark text-xs dark:bg-white/10 dark:text-white">
            <InfoOutlinedIcon sx={{ fontSize: 16 }} aria-hidden />
            {`${t("İlim Meclisi ünvanları için grupta en az")} ${titles.minMembers} ${t(
              "üye olmalı"
            )} (${titles.memberCount}/${titles.minMembers})`}
          </div>
        ) : null}
        <p className={`text-xs leading-4 ${soft.muted}`}>
          {`${t("Meclis günü: aynı gün en az")} ${titles.requiredParticipants} ${t(
            "üyenin kayıt girip hepsinin hedeflerini tamamlaması"
          )}`}
        </p>
      </div>

      <ul className="flex-1 min-h-0 overflow-y-auto">
        {(titles.list || []).map((title) => {
          const progress = titleProgress(title);
          return (
            <li key={title.code}>
              <button
                type="button"
                data-title={title.code}
                onClick={() => setSelected({ ...title, family: title.family || "meclis" })}
                className={soft.rowButton}
              >
                {titleImageOf(title, "meclis") ? (
                  <Medal title={title} family="meclis" size={44} />
                ) : (
                <span
                  className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                    title.earned ? "text-white" : "bg-terakkiSurfaceAlt text-terakkiNeutral dark:bg-white/10 dark:text-white/70"
                  }`}
                  style={title.earned ? { backgroundColor: title.color } : undefined}
                >
                  {title.earned ? (
                    <Groups2Icon sx={{ fontSize: 18 }} aria-hidden />
                  ) : (
                    <LockOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />
                  )}
                </span>
                )}
                <span className="flex-1 min-w-0">
                  <span
                    className={`block text-[15px] font-medium truncate ${
                      title.earned ? soft.ink : soft.muted
                    }`}
                  >
                    {title.name}
                  </span>
                  <span className={`block text-xs leading-4 line-clamp-2 ${soft.faint}`}>
                    {title.description}
                  </span>
                </span>
                <span className="flex flex-col items-end gap-1.5 min-w-[64px]">
                  {title.earned ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-[#2E9E6A] dark:text-[#7BE3A8]">
                      <CheckCircleIcon sx={{ fontSize: 14 }} aria-hidden />
                      {t("Kazanıldı")}
                    </span>
                  ) : (
                    <>
                      <span className={`text-[13px] font-medium ${soft.accent}`}>
                        {conditionText(limitingCondition(title, titles.family?.unit))}
                      </span>
                      <span className={`w-16 ${soft.track}`}>
                        <span
                          className={`block ${soft.fill}`}
                          style={{ width: `${Math.round(progress * 100)}%` }}
                        />
                      </span>
                    </>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <TitleDialog
        title={selected}
        unit={titles.family?.unit || ""}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
